import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { ConversationService } from '../service/core/ConversationService.service';
import { PermissionStore } from '../service/core/permission-store';

/** One pop-out chat window in the dock. */
export interface DockWindow {
  waId: string;
  /** The conversation row, as the list returns it (name, contactPhone, channel, handoff…). */
  conv: any;
  minimised: boolean;
}

/**
 * State for the floating chat dock: which panel/windows are open, and the unread badge.
 *
 * <p>Lives in root injector rather than in the dock component so it SURVIVES navigation — the whole
 * point of the dock is that an agent can walk between screens without losing the chat they are in
 * the middle of. The component is a thin renderer over this.</p>
 */
@Injectable({ providedIn: 'root' })
export class ChatDockService {

  private conversationService = inject(ConversationService);
  private perms = inject(PermissionStore);
  private router = inject(Router);

  /** Above this many open windows the dock stops accepting new ones.
   *
   *  <p>Not cosmetic: each open window runs its own 5-second message poll, on every page, for every
   *  signed-in agent. Three is already 36 requests a minute per user; an uncapped dock would scale
   *  that by however many chats someone happens to leave open.</p> */
  static readonly MAX_WINDOWS = 3;

  /** Is the conversation-list panel showing? */
  listOpen = false;

  /** Open chat windows, newest last (rendered right-to-left along the bottom). */
  windows: DockWindow[] = [];

  /** Unread total across the inbox — the number on the launcher. */
  unread = 0;

  /** True while the user is ON the Live Conversations page, where the dock is redundant. */
  onInboxPage = false;

  private pollTimer: any = null;

  constructor() {
    this.onInboxPage = this.isInbox(this.router.url);
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => { this.onInboxPage = this.isInbox(e.urlAfterRedirects || e.url); });
  }

  private isInbox(url: string): boolean {
    return (url || '').split('?')[0].replace(/\/+$/, '').endsWith('/conversations');
  }

  /** Whether this user may use the dock at all. Mirrors the sidebar's Conversations gate, so the
   *  dock never appears for someone who cannot open the inbox. Agents are additionally scoped
   *  server-side to their own chats, so this grants no new access. */
  get allowed(): boolean {
    return this.perms.unrestricted || this.perms.menuAllowed('/conversations');
  }

  /** Should the launcher render right now? Hidden on the inbox page itself (a floating chat on top
   *  of the full chat screen is redundant and would cover its composer). */
  get visible(): boolean {
    return this.allowed && !this.onInboxPage;
  }

  get atWindowLimit(): boolean {
    return this.windows.length >= ChatDockService.MAX_WINDOWS;
  }

  // ── badge ───────────────────────────────────────────────────────────────
  /** Starts the unread poll. Same endpoint and 60s cadence the sidebar badge already uses, so this
   *  adds one light request a minute and nothing per-page. */
  startBadgePoll(): void {
    if (this.pollTimer || !this.allowed) return;
    this.refreshUnread();
    this.pollTimer = setInterval(() => this.refreshUnread(), 60_000);
  }

  stopBadgePoll(): void {
    if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
  }

  refreshUnread(): void {
    this.conversationService.getUnreadTotal().subscribe({
      next: (res: any) => { this.unread = Number(res?.data ?? res ?? 0) || 0; },
      error: () => { /* badge is best-effort */ }
    });
  }

  // ── windows ─────────────────────────────────────────────────────────────
  /** Open (or focus) a chat. Returns false when the window cap is reached. */
  openChat(conv: any): boolean {
    const waId = conv?.waId;
    if (!waId) return false;

    const existing = this.windows.find(w => w.waId === waId);
    if (existing) { existing.minimised = false; existing.conv = conv; return true; }
    if (this.atWindowLimit) return false;

    this.windows.push({ waId, conv, minimised: false });
    // Opening a chat clears its badge for everyone (shared read marker), same as the inbox.
    if (conv.unreadCount) conv.unreadCount = 0;
    this.conversationService.markConversationRead(waId).subscribe({ error: () => {} });
    this.refreshUnread();
    return true;
  }

  closeChat(waId: string): void {
    this.windows = this.windows.filter(w => w.waId !== waId);
  }

  toggleMinimise(waId: string): void {
    const w = this.windows.find(x => x.waId === waId);
    if (w) w.minimised = !w.minimised;
  }

  toggleList(): void {
    this.listOpen = !this.listOpen;
    if (this.listOpen) this.refreshUnread();
  }

  /** Close everything — used when the dock is hidden (navigating onto the inbox page). */
  closeAll(): void {
    this.listOpen = false;
    this.windows = [];
  }
}
